import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, CalendarCheck, Users, ArrowRight, Sparkles, MapPin } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { SearchBar } from "@/components/SearchBar";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const OWNER_BENEFITS = [
  { icon: TrendingUp, title: "Gagnez en visibilité", desc: "Vos véhicules exposés à des milliers de clients à la recherche d'une location." },
  { icon: Users, title: "Recevez des clients", desc: "Un vrai canal d'acquisition : les demandes arrivent directement dans votre espace." },
  { icon: CalendarCheck, title: "Gérez votre activité", desc: "Disponibilités, réservations, suivi des locations et outils commerciaux." },
];

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative lr-gradient-dark overflow-hidden">
        <div className="absolute inset-0 lr-grain opacity-60" />
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt=""
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" style={{ color: "#D97706" }} /> Premier lancement au Maroc 🇲🇦
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              Comparez, choisissez et réservez votre véhicule chez les{" "}
              <span style={{ color: "#D97706" }}>loueurs indépendants</span>.
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-2xl leading-relaxed">
              <span style={{ color: "#C1272D" }} className="font-semibold">link</span> & <span style={{ color: "#006233" }} className="font-semibold">rent</span> met en relation les clients et les loueurs automobiles professionnels indépendants. Un véhicule pour chaque besoin, des prix transparents, des conditions claires.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-10"
          >
            <SearchBar />
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" style={{ color: "#006233" }} /> Loueurs vérifiés</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" style={{ color: "#C1272D" }} /> Partout au Maroc</span>
            <span className="flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-amber-500" /> Sans commission cachée</span>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Sélection du moment</p>
            <h2 className="mt-1 font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Véhicules disponibles</h2>
          </div>
          <Button variant="outline" onClick={() => navigate("/cars")} data-testid="home-see-all-btn" className="hidden sm:inline-flex">
            Tout voir <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {featured.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      </section>

      {/* OWNER PITCH */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#006233" }}>Espace Loueur</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                « Vous avez les voitures. Nous vous aidons à trouver les clients et à gérer votre activité. »
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Une multitude de petits loueurs possèdent une flotte disponible mais disposent de moyens limités pour la commercialiser. link&rent devient votre canal d'acquisition client et votre outil de gestion.
              </p>
              <div className="mt-8 space-y-5">
                {OWNER_BENEFITS.map((b) => (
                  <div key={b.title} className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "#0B132B" }}>
                      <b.icon className="h-5 w-5 text-white" />
                    </span>
                    <div>
                      <h4 className="font-semibold text-slate-900">{b.title}</h4>
                      <p className="text-sm text-slate-600">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex gap-3">
                <Button onClick={() => navigate("/register")} data-testid="home-register-btn" className="text-white" style={{ background: "#006233" }}>
                  Publier mon agence
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758411898312-8592bb81e30d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Flotte de véhicules"
                className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 rounded-2xl bg-white p-5 shadow-xl border border-slate-100 hidden sm:block">
                <p className="text-xs text-slate-500">Taux d'utilisation du parc</p>
                <p className="font-display text-3xl font-extrabold" style={{ color: "#006233" }}>+38%</p>
                <p className="text-xs text-slate-400">en moyenne avec link&rent</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
